import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Determine the API URL based on environment
const getApiUrl = () => {
  // For production build (AWS deployment)
  if (process.env.NODE_ENV === 'production') {
    // In production, API is at the same domain
    // We're using MongoDB Atlas as specified in the .env file
    return window.location.origin;
  }
  // For local development
  return 'http://localhost:5000';
};

// Create a custom axios instance with specific configuration
const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000, // 30 seconds timeout for longer operations
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  },
  withCredentials: false // Disable withCredentials to avoid CORS issues
});

// Replace the global axios with our configured instance
Object.assign(axios, api);

// Log connection information
console.log('Axios configured with baseURL:', axios.defaults.baseURL);

// Simplified token management
const getToken = () => localStorage.getItem('token');

// Function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true; // If we can't decode the token, assume it's expired
  }
};

// Function to handle token expiration
const handleTokenExpiration = () => {
  console.log('Token expired or invalid, redirecting to login');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

// Add a request interceptor to include the auth token in all requests
axios.interceptors.request.use(
  config => {
    // Skip token check for auth endpoints
    const skipTokenCheck = [
      '/api/auth',
      '/api/auth/login',
      '/api/auth/forgot-password',
      '/api/auth/reset-password',
      '/api/users/register'
    ];
    
    if (skipTokenCheck.some(endpoint => config.url.includes(endpoint))) {
      return config;
    }
    
    const token = getToken();
    
    // Check if token exists and is not expired
    if (token) {
      if (isTokenExpired(token)) {
        handleTokenExpiration();
      } else {
        config.headers['x-auth-token'] = token;
      }
    }
    
    return config;
  },
  error => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor for better error handling
axios.interceptors.response.use(
  response => response,
  error => {
    // Log detailed error information
    if (error.response) {
      console.error('Response error:', {
        status: error.response.status,
        data: error.response.data
      });
      
      // Handle unauthorized errors
      if (error.response.status === 401) {
        handleTokenExpiration();
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
      console.error('Server might be down or network issues. Please check your connection.');
    } else {
      console.error('Request setup error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

export default axios;
